import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { FiEye, FiHeart, FiShuffle } from "react-icons/fi";
import { formatCount } from "./api";

const PER_BATCH = 4;
const ROTATE_MS = 10000;
const RESET_DELAY_MS = 1000;
// Soft colour washes that cross-fade behind the row when the batch changes.
const BACKDROPS = [
  "linear-gradient(120deg, #1fb5a8 0%, #f0a25a 100%)",
  "linear-gradient(120deg, #6a5cff 0%, #ff6fb1 55%, #ffb36b 100%)",
  "linear-gradient(120deg, #ff5f9e 0%, #ffd3a8 100%)",
  "linear-gradient(120deg, #2b6cff 0%, #38d0ff 100%)",
  "linear-gradient(120deg, #8de35a 0%, #f5e05e 100%)",
  "linear-gradient(120deg, #0f766e 0%, #2b4c7e 100%)",
  "linear-gradient(120deg, #3b82f6 0%, #34d399 100%)",
  "linear-gradient(120deg, #ff8a3d 0%, #ff4d6d 100%)",
];

/**
 * A portrait card that turns towards the pointer inside a limited 3D range:
 * the card rotates up to +/-15deg while the cover slides the opposite way for parallax.
 * Pointer offsets are written to CSS variables so no React re-render happens on mouse move.
 */
function TiltCard({ character: c }) {
  const location = useLocation();
  const wrap = useRef(null);
  const timer = useRef(0);
  const [broken, setBroken] = useState(false);
  const description = c.description?.trim() || "Explore this character's story.";

  useEffect(() => () => clearTimeout(timer.current), []);

  const setTilt = (x, y) => {
    const el = wrap.current;
    if (!el) return;
    el.style.setProperty("--tx", x.toFixed(4));
    el.style.setProperty("--ty", y.toFixed(4));
  };
  const onMove = (e) => {
    const box = wrap.current.getBoundingClientRect();
    if (!box.width || !box.height) return;
    // Offset from the card centre as a fraction of its size: -0.5 .. 0.5.
    const x = Math.max(-0.5, Math.min(0.5, (e.clientX - box.left) / box.width - 0.5));
    const y = Math.max(-0.5, Math.min(0.5, (e.clientY - box.top) / box.height - 0.5));
    setTilt(x, y);
  };
  const onLeave = () => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setTilt(0, 0), RESET_DELAY_MS);
  };

  return (
    <div
      ref={wrap}
      className="tilt-wrap"
      onMouseMove={onMove}
      onMouseEnter={() => clearTimeout(timer.current)}
      onMouseLeave={onLeave}
    >
      <Link
        className="tilt-card"
        to={`/character/${c.publicId}`}
        state={{ from: location.pathname + location.search }}
        aria-label={`View ${c.name}`}
      >
        <span className="tilt-cover">
          {!broken && (
            <img src={c.card} alt="" onError={() => setBroken(true)} />
          )}
        </span>
        <span className="tilt-info">
          <strong className="tilt-name">{c.name}</strong>
          <span className="tilt-stats">
            <span>
              <FiHeart aria-hidden="true" />
              {formatCount(c.stats.likes)}
              <span className="sr-only"> likes</span>
            </span>
            <span className="tilt-dot" aria-hidden="true" />
            <span>
              <FiEye aria-hidden="true" />
              {formatCount(c.stats.visits)}
              <span className="sr-only"> visits</span>
            </span>
          </span>
          <span className="tilt-desc">{description}</span>
        </span>
      </Link>
    </div>
  );
}

export function ForYouSkeleton() {
  return (
    <div className="for-you" aria-busy="true" aria-label="Loading recommendations">
      <div className="for-you-head">
        <h2>Recommended for you</h2>
      </div>
      <div className="for-you-row">
        {Array.from({ length: PER_BATCH }, (_, i) => (
          <div key={i} className="tilt-wrap tilt-skeleton" />
        ))}
      </div>
    </div>
  );
}

/** Row of 4 tilt cards drawn from a pool; shuffles on demand and every 10s while idle. */
export default function ForYou({ pool }) {
  const [batch, setBatch] = useState(0);
  const [bg, setBg] = useState(() => Math.floor(Math.random() * BACKDROPS.length));
  const [paused, setPaused] = useState(false);
  const batches = Math.floor(pool.length / PER_BATCH);
  const cards = pool.slice(batch * PER_BATCH, batch * PER_BATCH + PER_BATCH);

  const next = useCallback(() => {
    setBatch((b) => (batches > 1 ? (b + 1) % batches : 0));
    setBg((v) => (v + 1 + Math.floor(Math.random() * (BACKDROPS.length - 1))) % BACKDROPS.length);
  }, [batches]);

  useEffect(() => {
    if (batches < 2 || paused) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    const id = setInterval(next, ROTATE_MS);
    return () => clearInterval(id);
  }, [batches, paused, next]);

  if (!cards.length) return null;
  return (
    <section className="for-you" aria-label="Recommended for you">
      <div className="for-you-bg" aria-hidden="true">
        {BACKDROPS.map((image, i) => (
          <span
            key={image}
            style={{ backgroundImage: image, opacity: i === bg ? 1 : 0 }}
          />
        ))}
      </div>
      <div className="for-you-head">
        <h2>Recommended for you</h2>
        {batches > 1 && (
          <button type="button" className="for-you-shuffle" onClick={next}>
            <FiShuffle aria-hidden="true" />
            Shuffle
          </button>
        )}
      </div>
      <div
        className="for-you-row"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocus={() => setPaused(true)}
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget)) setPaused(false);
        }}
      >
        {cards.map((c) => (
          <TiltCard key={c.publicId} character={c} />
        ))}
      </div>
    </section>
  );
}
